import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Phone, Mail, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useContent } from "@/context/ContentContext";

export const Footer = () => {
  const { content } = useContent();
  const socials = content.socials || {};

  return (
    <footer data-testid="site-footer" className="bg-black border-t border-gold/20">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 grid gap-12 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-4 mb-5">
            <Logo size="lg" testId="footer-logo" />
            <div>
              <p className="font-accent text-2xl font-bold gold-text">B&amp;H</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Truck Dispatching</p>
            </div>
          </div>
          <p className="font-accent uppercase text-champagne text-xs tracking-[0.15em] leading-relaxed">
            “Driven by Integrity. Powered by Precision. Committed to Your Success.”
          </p>
        </div>

        <div>
          <p className="overline-label mb-5">Navigate</p>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-zinc-400">
            {[
              ["Home", "/"],
              ["About Us", "/#about"],
              ["Our Services", "/#services"],
              ["Equipment Types", "/#equipment"],
              ["Become a Carrier", "/become-a-carrier"],
              ["FAQ", "/#faq"],
              ["Contact", "/contact"],
              ["Admin", "/admin"],
            ].map(([label, to]) => (
              <li key={label}>
                <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className="hover:text-champagne transition-colors duration-300">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="overline-label mb-5">Contact</p>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li className="flex items-center gap-3">
              <Phone size={15} className="text-gold shrink-0" />
              <a href={`tel:${content.phone}`} data-testid="footer-phone-link" className="hover:text-champagne transition-colors duration-300">{content.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={15} className="text-gold shrink-0" />
              <a href={`mailto:${content.email}`} data-testid="footer-email-link" className="hover:text-champagne transition-colors duration-300">{content.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <MapPin size={15} className="text-gold shrink-0" />
              <span data-testid="footer-location">{content.location}</span>
            </li>
          </ul>
          {(socials.facebook || socials.instagram || socials.linkedin) && (
            <div className="flex gap-3 mt-6">
              {socials.facebook && (
                <a href={socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-social-facebook" className="p-2 border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300">
                  <Facebook size={16} />
                </a>
              )}
              {socials.instagram && (
                <a href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-social-instagram" className="p-2 border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300">
                  <Instagram size={16} />
                </a>
              )}
              {socials.linkedin && (
                <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-social-linkedin" className="p-2 border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300">
                  <Linkedin size={16} />
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <p>© {new Date().getFullYear()} B&amp;H Truck Dispatching. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-champagne transition-colors duration-300">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-champagne transition-colors duration-300">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
